import React, { memo, useCallback } from 'react';
import S from '../style/Dac7TabContent';
import Dac7Dropdown from './Dac7Dropdown';
import countries from '../utils/countriesList';
import Dac7Input from './Dac7Input';
import V from '../utils/validation';
import { SellerBusinessDac7 } from '../../../../../../typings/dac7';
import { useI18n } from '../../../../../i18n';
import useDac7ArrayItems from '../hooks/useDac7ArrayItems';
import Dac7RemoveItemButton from './Dac7RemoveItemButton';
import Dac7AddItemButton from './Dac7AddItemButton';

interface Props {
  taxDetails: Readonly<SellerBusinessDac7['taxDetails']>;
}

const Dac7TaxDetails = ({ taxDetails: taxDetailsInitial }: Props) => {
  const { items: taxDetails, addItem, removeItem } = useDac7ArrayItems(taxDetailsInitial);
  const i18n = useI18n();

  const addTaxDetail = useCallback(() => {
    addItem({ taxId: '', taxIdIssuerCountry: '', vat: '', businessRegistrationNumber: '' });
  }, []);

  const removeTaxDetail = useCallback(i => {
    removeItem(i);
  }, []);

  return (
    <>
      {taxDetails.map((taxDetail, i) => (
        <S.Dac7Fieldset className="dac7-tax-details" key={`dac7_taxDetails_${taxDetail.feId}`}>
          <S.Dac7FormField>
            <Dac7Input
              name={`dac7_taxDetails.${i}.taxId`}
              label={i18n('dlv_dac7_taxDetails_taxId')}
              pattern={V.getMinCharsPattern(2)}
              required
              value={taxDetail.taxId}
              disabled={!!taxDetail.taxId}
            />
          </S.Dac7FormField>
          <S.Dac7FormField>
            <Dac7Dropdown
              sourceItems={countries}
              initialSelectedIds={[taxDetail.taxIdIssuerCountry]}
              name={`dac7_taxDetails.${i}.taxIdIssuerCountry`}
              label={i18n('dlv_dac7_taxDetails_taxIdIssuerCountry')}
              required
              disabled={!!taxDetail.taxIdIssuerCountry}
              requiredMessage={i18n('dlv_dac7_taxDetails_taxIdIssuerCountryRequired')}
              placeholder={i18n('dlv_dac7_taxDetails_taxIdIssuerCountryPlaceholder')}
            />
          </S.Dac7FormField>
          <S.Dac7FormField>
            <Dac7Input
              name={`dac7_taxDetails.${i}.vat`}
              label={i18n('dlv_dac7_taxDetails_vat')}
              pattern={V.getMinLettersOrNumbersPattern(2)}
              value={taxDetail.vat}
              disabled={!!taxDetail.vat}
            />
          </S.Dac7FormField>
          <S.Dac7FormField>
            <Dac7Input
              name={`dac7_taxDetails.${i}.businessRegistrationNumber`}
              label={i18n('dlv_dac7_taxDetails_businessRegistrationNumber')}
              pattern={V.getMinCharsPattern(2)}
              value={taxDetail.businessRegistrationNumber}
              disabled={!!taxDetail.businessRegistrationNumber}
            />
          </S.Dac7FormField>
          {i === 0 || !!taxDetail.taxId ? null : (
            <Dac7RemoveItemButton
              onClick={() => removeTaxDetail(i)}
              label={i18n('dlv_dac7_remove_tax_details_label')}
            />
          )}
        </S.Dac7Fieldset>
      ))}
      <Dac7AddItemButton onClick={addTaxDetail} label={i18n('dlv_dac7_add_tax_details_label')} />
      <S.StyledRequiredFieldsText>
        {i18n(`dlv_dac7_required_fields_description`)}
      </S.StyledRequiredFieldsText>
    </>
  );
};

export default memo(Dac7TaxDetails);
